import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../services/api";

const GENDERS = ["", "Male", "Female", "Other"];

/* ── Helpers defined outside to prevent focus loss on re-render ── */
function FormField({ label, value, onChange, placeholder, error, type, maxLength }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type || "text"}
        className={`input ${error ? "border-red-400 focus:ring-red-400" : ""}`}
        placeholder={placeholder || ""}
        value={value}
        maxLength={maxLength}
        onChange={e => onChange(e.target.value)}
        autoComplete="off"
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function InfoBadge({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg px-4 py-2.5">
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-gray-700">{value || "—"}</p>
    </div>
  );
}

export default function PatientEdit() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [patient,   setPatient]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState("");

  const [form,    setForm]    = useState({ name: "", age: "", gender: "", phone: "", address: "" });
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [apiError, setApiError] = useState("");

  /* Delete flow */
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);

  /* ── Load patient on mount ── */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await api.get(`/patients/${patientId}`);
        const p = res.data;
        setPatient(p);
        setForm({
          name:    p.name    || "",
          age:     p.age     != null ? String(p.age) : "",
          gender:  p.gender  || "",
          phone:   p.phone   || "",
          address: p.address || "",
        });
      } catch (err) {
        setLoadError(err.response?.data?.message || "Failed to load patient.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [patientId]);

  /* ── Field setter ── */
  const setField = (key, value) => {
    setForm(p => ({ ...p, [key]: value }));
    setSaved(false);
    if (errors[key]) setErrors(p => ({ ...p, [key]: "" }));
    setApiError("");
  };

  /* ── Validation ── */
  const validate = () => {
    const e = {};
    if (!form.name.trim()) {
      e.name = "Name is required";
    } else if (!/^[a-zA-Z\s'.\\-]+$/.test(form.name.trim())) {
      e.name = "Name should contain only letters and spaces";
    }
    if (form.phone && !/^\d{10}$/.test(form.phone.trim())) {
      e.phone = "Phone number must be exactly 10 digits";
    }
    if (form.age && (isNaN(form.age) || Number(form.age) <= 0 || Number(form.age) > 130)) {
      e.age = "Please enter a valid age (1–130)";
    }
    return e;
  };

  /* ── Save ── */
  const save = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    setApiError("");
    try {
      const res = await api.put(`/patients/${patient._id}`, {
        name:    form.name.trim(),
        age:     form.age ? Number(form.age) : undefined,
        gender:  form.gender,
        phone:   form.phone,
        address: form.address,
      });
      setPatient(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setApiError(err.response?.data?.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/patients/${patient._id}`);
      navigate("/staff-portal/patient/search", { replace: true });
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed.");
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  /* ── Render states ── */
  if (loading) return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="text-gray-400 text-sm">Loading patient…</div>
    </div>
  );

  if (loadError) return (
    <div className="card p-8 text-center">
      <p className="text-red-500 mb-3">{loadError}</p>
      <button onClick={() => navigate(-1)} className="btn text-sm">← Go Back</button>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-xl space-y-6"
    >
      {/* ── Page header ── */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">✏️ Edit Patient</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Update details for <span className="font-medium text-gray-700">{patient.name}</span>
          </p>
        </div>
        <button onClick={() => navigate(-1)} className="btn text-sm">← Back</button>
      </div>

      {/* ── Read-only identity info ── */}
      <div className="grid grid-cols-2 gap-3">
        <InfoBadge label="Patient ID" value={patient.patientId} />
        <InfoBadge
          label="Registered On"
          value={new Date(patient.createdAt).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric"
          })}
        />
      </div>

      {/* ── Edit form ── */}
      <div className="card space-y-4">
        <FormField
          label="Full Name *"
          value={form.name}
          onChange={v => setField("name", v)}
          placeholder="Patient's full name"
          error={errors.name}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="Age"
            value={form.age}
            onChange={v => setField("age", v.replace(/\D/g, ""))}
            placeholder="Age in years"
            error={errors.age}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
            <select
              className="input"
              value={form.gender}
              onChange={e => setField("gender", e.target.value)}
            >
              {GENDERS.map(g => (
                <option key={g} value={g}>{g || "— Select gender —"}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
          <input
            type="tel"
            className={`input ${errors.phone ? "border-red-400 focus:ring-red-400" : ""}`}
            placeholder="10-digit mobile number"
            value={form.phone}
            maxLength={10}
            onChange={e => setField("phone", e.target.value.replace(/\D/g, ""))}
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            className="input"
            placeholder="Patient's address (optional)"
            rows={2}
            value={form.address}
            onChange={e => setField("address", e.target.value)}
          />
        </div>

        {apiError && (
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2 text-sm">
            {apiError}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary flex-1 disabled:opacity-60"
          >
            {saving ? "Saving…" : saved ? "✓ Saved!" : "Save Changes"}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/staff-portal/patient/history/${patient.patientId}`)}
            className="btn px-4"
          >
            View History
          </button>
        </div>
      </div>

      {/* ── Danger zone ── */}
      <div className="border border-red-200 rounded-xl p-4 space-y-3 bg-red-50">
        <div>
          <p className="font-semibold text-red-700 text-sm">⚠️ Danger Zone</p>
          <p className="text-xs text-red-500 mt-0.5">
            Deleting a patient is permanent. All prescriptions and visit history will also be removed.
            The patient ID <strong>{patient.patientId}</strong> will not be reused — the next new patient will receive a new sequential ID.
          </p>
        </div>

        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="px-4 py-2 text-sm bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-100 transition font-medium"
          >
            🗑️ Delete This Patient
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium text-red-800">
              Are you sure you want to delete <strong>{patient.name}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 transition font-semibold"
              >
                {deleting ? "Deleting…" : "Yes, Delete Permanently"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="btn text-sm px-4"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
